// src/constants/i18n.factory.ts
// ============================================================================
// i18n Factory — ONE implementation, works for he/en as long as locale shape matches
// ============================================================================

import type { AlbumName, SectionFourSEO, SeoService } from '@/constants/core';
import {
  ALBUM,
  BASE_URL,
  BUSINESS_BASE,
  EVENT_GROUP,
  SECONDARY_KEYWORD_BY_SERVICE,
  preferredMapsUrl,
  SEO_SERVICE_BAR_BAT,
  SEO_SERVICE_EVENT,
  SEO_SERVICE_HENNA,
  SEO_SERVICE_WEDDING,
} from '@/constants/core';
import { isRegionName, type Region, type SeoCity } from '@/constants/geo';
import {
  buildGlobalSeoDescription,
  buildLocalSeoDescription,
} from '@/constants/seo-description-templates';
import { he as heLocale } from './he';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

// Use Hebrew locale as canonical structure
type LocaleShape = {
    meta: {
      lang: string;
      langFull: string;
      dir: 'rtl' | 'ltr';
      name: string;
    };
  
    words: any;
    brand: any;
    business: any;
  
    home: any;
    footer: any;
  
    section1: any;
    section2: any;
    section3: any;
    section4: any;
    section5: any;
    section6: any;
    planning: any;
  
    metaDescription: any;
    menu:any;
    seo:any
    geo:any
  };


/**
 * Locale-aware location prefix:
 * - he: "ב${city}"
 * - en: "in ${city}"
 * - fr: "à ${city}"
 * - ru: "в ${city}"
 */
function makeCityPrefix(locale: LocaleShape, cityOrLabel: string) {
  const name = String(cityOrLabel ?? '').trim();
  if (!name) return '';

  if (locale.meta.lang === 'he') return `ב${name}`;
  if (locale.meta.lang === 'fr') return `à ${name}`;
  if (locale.meta.lang === 'ru') return `в ${name}`;

  return `in ${name}`;
}

function makeSentenceCityPrefix(locale: LocaleShape, cityOrLabel: string) {
  const name = String(cityOrLabel ?? '').trim();
  if (!name) return '';

  if (locale.meta.lang === 'he') return `ב${name},`;
  if (locale.meta.lang === 'fr') return `À ${name},`;
  if (locale.meta.lang === 'ru') return `В ${name},`;

  return `In ${name},`;
}

/**
 * Same as above but returns the prefix *including leading space when needed*
 * so you can safely do: `${keyword}${cityBet}`
 */
function makeCitySuffix(locale: LocaleShape, cityOrLabel: string) {
  const pref = makeCityPrefix(locale, cityOrLabel);
  if (!pref) return '';

  return ` ${pref}`;     // תמיד עם רווח
}



export type AppLang = 'he' | 'en' | 'ru' | 'fr';

export type I18nConstants = ReturnType<typeof createI18nConstants>;

export function createI18nConstants(locale: LocaleShape) {

  function translateKeywordForLocale(keyword: string) {
    const k = String(keyword ?? '').trim();
  
    if (!k) return '';
  
    // אם באנגלית — החזר DJ
    if (locale.meta.lang === 'en') {
      const lower = k.toLowerCase();
  
      if (
        lower.includes('dj') ||
        lower.includes('די') ||
        lower.includes('ג׳יי')
      ) {
        return WORDS.dj.en; // "DJ"
      }
    }
  
    return k;
  }
  function cityBetForLocale(cityOrLabel: SeoCity | string) {
    const raw = String(cityOrLabel ?? '').trim();
    if (!raw) return '';
  
    const translated = translateLocationLabel(raw);
  
    // Uses the locale of THIS factory instance
    return makeCitySuffix(locale, translated);
  }
  // ─────────────────────────────────────────────────────────────
  // Brand / Words
  // ─────────────────────────────────────────────────────────────
  const WORDS = locale.words;
  const FIRST_NAME = locale.brand.firstName;
  const BRAND_TITLE_SHORT = locale.brand.short;
  const BRAND_TITLE_HE = locale.brand.title;

  // ─────────────────────────────────────────────────────────────
  // BUSINESS
  // ─────────────────────────────────────────────────────────────
  const BUSINESS = {
    name: locale.business.name,

    phone: BUSINESS_BASE.phone,
    email: BUSINESS_BASE.email,

    address: {
      street: locale.business.address.street,
      city: locale.business.address.city,
      postal: locale.business.address.postal,
      country: locale.business.address.country,

      lat: BUSINESS_BASE.address.lat,
      lng: BUSINESS_BASE.address.lng,
      mapUrl: BUSINESS_BASE.address.mapUrl,
    },

    googlePlaceId: BUSINESS_BASE.googlePlaceId,
    sameAs: BUSINESS_BASE.sameAs,
  } as const;

  // ─────────────────────────────────────────────────────────────
  // HTML / Home / Footer
  // ─────────────────────────────────────────────────────────────
  const HTML_LANG = locale.meta.lang;
  const HTML_LANG_FULL = locale.meta.langFull;

  const HOME_TITLE = locale.home.title;
  const HOME_DESC = locale.home.description;
  const HOME_HERO_TITLE = locale.home.heroTitle;

  const FOOTER_TEXT = locale.footer.copyright(new Date().getFullYear());

  // ─────────────────────────────────────────────────────────────
  // Section 2
  // ─────────────────────────────────────────────────────────────
  const SECTION_TWO_TITLE = locale.section2.title;
  const SECTION_TWO_DESCRIPTION = locale.section2.description;
  const SECTION_TWO_ADDITIONAL_TEXT = locale.section2.additionalText;
  const SECTION_TWO_IMG_ALT = locale.section2.imageAltPortrait;
  const SECTION_TWO_HORIZONTAL_IMG_ALT = locale.section2.imageAltHorizontal;

  // ─────────────────────────────────────────────────────────────
  // Section 3
  // ─────────────────────────────────────────────────────────────
  const SECTION_THREE_TITLE = locale.section3.title;
  const SECTION_THREE_FALLBACK_TEXT = locale.section3.fallbackText;
  const SECTION_THREE_ALBUMS_ERROR = locale.section3.albumsError;
  const SECTION_THREE_FILES_ERROR = locale.section3.filesError;
  const SECTION_THREE_LOADING_TEXT = locale.section3.loading;
  const SECTION_THREE_VIDEO_LABEL = locale.section3.navLabels.video;
  const SECTION_THREE_PHOTOS_LABEL = locale.section3.navLabels.photos;
  const SECTION_THREE_NEXT_ITEM_LABEL = locale.section3.navLabels.nextItem;
  const SECTION_THREE_PREV_ITEM_LABEL = locale.section3.navLabels.prevItem;
  const SECTION_THREE_CAROUSEL_LABEL = locale.section3.navLabels.carousel;
  const SECTION_THREE_CURRENT_ITEM_LABEL = locale.section3.navLabels.currentItem;

  const albumDescriptions: Record<AlbumName, string> = {
    חתונות: locale.section3.albumDescriptions.weddings,
    חינות: locale.section3.albumDescriptions.hennas,
    'בר/בת מצוות': locale.section3.albumDescriptions.barBat,
    פורום: locale.section3.albumDescriptions.forum,
    כתבות: locale.section3.albumDescriptions.articles,
    המלצות: locale.section3.albumDescriptions.recommendations,
    ברים: locale.section3.albumDescriptions.bars,
    פלאיירים: locale.section3.albumDescriptions.flyers,
  };

  const albumsText: Record<AlbumName, string> = {
    חתונות: locale.section3.albumTexts.weddings,
    חינות: locale.section3.albumTexts.hennas,
    'בר/בת מצוות': locale.section3.albumTexts.barBat,
    פורום: locale.section3.albumTexts.forum,
    כתבות: locale.section3.albumTexts.articles,
    המלצות: locale.section3.albumTexts.recommendations,
    ברים: locale.section3.albumTexts.bars,
    פלאיירים: locale.section3.albumTexts.flyers,
  };

  // ─────────────────────────────────────────────────────────────
  // Section 4
  // ─────────────────────────────────────────────────────────────
  const SECTION_FOUR_TITLE = locale.section4.title;
  const SECTION_FOUR_DESCRIPTION = locale.section4.description;

  const SECTION_FOUR_CONTACT_GROUP_TITLE = locale.section4.groupTitles.contact;
  const SECTION_FOUR_EVENTS_GROUP_TITLE = locale.section4.groupTitles.events;
  const SECTION_FOUR_SERVICES_GROUP_TITLE = locale.section4.groupTitles.services;

  // Contact buttons
  const telHref = `tel:${BUSINESS.phone.replace(/[^+\d]/g, '')}`;

  const ig = BUSINESS.sameAs.find((u: any) => u.includes('instagram.com'));
  const fb = BUSINESS.sameAs.find((u: any) => u.includes('facebook.com'));

  const SECTION_FOUR_CONTACT_BUTTONS = {
    phone: { label: locale.section4.contactButtons.phone, href: telHref },
    whatsapp: {
      label: locale.section4.contactButtons.whatsapp,
      href: 'https://wa.me/972528623221',
    },
    email: {
      label: locale.section4.contactButtons.email,
      href: `mailto:${BUSINESS.email}`,
    },
    instagram: {
      label: locale.section4.contactButtons.instagram,
      href: ig ?? BASE_URL,
    },
    facebook: {
      label: locale.section4.contactButtons.facebook,
      href: fb ?? BASE_URL,
    },
    navigate: {
      label: locale.section4.contactButtons.navigate,
      href: preferredMapsUrl(),
    },
  } as const;

  // Event buttons
  const SECTION_FOUR_EVENT_BUTTONS = [
    {
      label: WORDS.events.wedding.many,
      icon: 'ring',
      prefill: locale.section4.prefills.wedding,
    },
    {
      label: WORDS.events.henna.many,
      icon: 'vest',
      prefill: locale.section4.prefills.henna,
    },
    {
      label: WORDS.events.bar_bat.many,
      icon: 'synagogue',
      prefill: locale.section4.prefills.barBat,
    },
    {
      label: WORDS.events.generic.many,
      icon: 'glass',
      prefill: locale.section4.prefills.generic,
    },
  ] as const;

  // Service buttons
  const SECTION_FOUR_SERVICE_BUTTONS = [
    { label: locale.section4.services.sound.label, icon: 'volume', prefill: '' },
    { label: locale.section4.services.light.label, icon: 'light', prefill: '' },
    { label: locale.section4.services.attractions.label, icon: 'star', prefill: '' },
    { label: locale.section4.services.recording.label, icon: 'mic', prefill: '' },
  ] as const;

  // ─────────────────────────────────────────────────────────────
  // Section 5
  // ─────────────────────────────────────────────────────────────
  const SECTION_FIVE_TITLE = locale.section5.title;
  const SECTION_FIVE_LINKS_HEADING = locale.section5.linksHeading;

  // ─────────────────────────────────────────────────────────────
  // Meta description
  // ─────────────────────────────────────────────────────────────
  function makeMetaDescription(service: string, location?: string) {
    const loc = location?.trim();
    const locText = loc ? makeCitySuffix(locale, translateLocationLabel(loc)) : '';

    if (locale.meta.lang === 'he') {
      return loc
        ? buildLocalSeoDescription(service as SeoService, translateLocationLabel(loc))
        : buildGlobalSeoDescription(service as SeoService);
    }

    const map = locale.metaDescription;

    if (service === 'חתונה') return `${map.wedding}${locText}`;
    if (service === 'חינה') return `${map.henna}${locText}`;
    if (service === 'בר-מצווה') return `${map.barBat}${locText}`;

    return `${map.generic}${locText}`;
  }

  // ─────────────────────────────────────────────────────────────
  // Brand regex
  // ─────────────────────────────────────────────────────────────
  const BRAND_PREFIX_RE = new RegExp(
    `^די ג['\\u05F3]יי\\s+${BRAND_TITLE_SHORT}\\s*\\|\\s*`,
    'u'
  );

  const BRAND_SUFFIX_RE = new RegExp(
    `\\s*\\|\\s*די ג['\\u05F3]יי\\s+${BRAND_TITLE_SHORT}\\s*$`,
    'u'
  );

  // ─────────────────────────────────────────────────────────────
  // SEO keyword variants
  // ─────────────────────────────────────────────────────────────
  const SEO_KEYWORD_VARIANTS = {
    חתונה: [
      `${WORDS.mc} ${WORDS.events.wedding.to}`,
      `${WORDS.dj.he} ${WORDS.events.wedding.to}`,
      `${WORDS.dj.en} ${WORDS.events.wedding.to}`,
    ],
    חינה: [
      `${WORDS.mc} ${WORDS.events.henna.to}`,
      `${WORDS.dj.he} ${WORDS.events.henna.to}`,
      `${WORDS.dj.en} ${WORDS.events.henna.to}`,
    ],
    'בר-מצווה': [
      `${WORDS.mc} ${WORDS.events.bar_bat.to}`,
      `${WORDS.dj.he} ${WORDS.events.bar_bat.to}`,
      `${WORDS.dj.en} ${WORDS.events.bar_bat.to}`,
    ],
    אירוע: [
      `${WORDS.mc} ${WORDS.events.generic.to}`,
      `${WORDS.dj.he} ${WORDS.events.generic.to}`,
      `${WORDS.dj.en} ${WORDS.events.generic.to}`,
    ],
  } as const;

  function preferredKeyword(service: string) {
    const arr = SEO_KEYWORD_VARIANTS[service as keyof typeof SEO_KEYWORD_VARIANTS];
    return arr?.[0] ?? service;
  }

  function makeTitle(keyword: string, city?: string) {
    const loc = city?.trim();
    return loc ? `${keyword} ב${loc} - ${BRAND_TITLE_HE}` : `${keyword} - ${BRAND_TITLE_HE}`;
  }

  const preferredKeywordForService = (service: SeoService): string => {
    const arr = SEO_KEYWORD_VARIANTS[service as keyof typeof SEO_KEYWORD_VARIANTS] as readonly string[];
    return arr.find((v) => v.includes(WORDS.mc)) ?? arr[0] ?? service;
  };

  const secondaryKeywordForService = (service: SeoService): string =>
    locale.meta.lang === 'he' ? SECONDARY_KEYWORD_BY_SERVICE[service] ?? '' : '';

  function buildWhatsAppWithText(raw: string): string {
    const normalized = raw.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
    const encoded = encodeURIComponent(normalized);
    const base = SECTION_FOUR_CONTACT_BUTTONS.whatsapp.href;
    return `${base}?text=${encoded}`;
  }

  function citySuffix(cityOrLabel: SeoCity | string) {
    // ⬅️ אם אתה רוצה באנגלית גם תרגום עיר (Tel Aviv, Ashdod...)
    const translated = translateLocationLabel(String(cityOrLabel));
    return makeCitySuffix(locale, translated);
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers (internal)
  // ─────────────────────────────────────────────────────────────
  const isGenericDjKeyword = (keyword?: string) =>
    !keyword ||
    keyword.trim() === '' ||
    keyword.trim() === WORDS.dj.he_alt ||
    keyword.trim() === WORDS.dj.he ||
    keyword.trim().toLowerCase() === WORDS.dj.en.toLowerCase();

  // ─────────────────────────────────────────────────────────────
  // EVENT TYPE DESCRIPTION (mobile / desktop)
  // ─────────────────────────────────────────────────────────────
  const SECTION_THREE_DEFAULT_ALBUM_BY_SERVICE: Record<SeoService, AlbumName> = {
    [SEO_SERVICE_WEDDING]: ALBUM.weddings,
    [SEO_SERVICE_HENNA]: ALBUM.hennas,
    [SEO_SERVICE_BAR_BAT]: ALBUM.barBat,
    [SEO_SERVICE_EVENT]: ALBUM.weddings,
  } as const;

  type EventTypeDescriptionVariant = {
    mobile: (city: SeoCity) => string;
    desktop: (city: SeoCity) => string;
  };

  const EVENT_TYPE_DESCRIPTION_COPY: Record<SeoService, EventTypeDescriptionVariant> = {
    [SEO_SERVICE_WEDDING]: {
      mobile: (city) =>
        locale.section5.eventTypeDescription.wedding.mobile(
          translateCity(city),
          citySuffix(city)
        ),
      desktop: (city) =>
        locale.section5.eventTypeDescription.wedding.desktop(
          translateCity(city),
          citySuffix(city)
        ),
    },
  
    [SEO_SERVICE_EVENT]: {
      mobile: (city) =>
        locale.section5.eventTypeDescription.event.mobile(
          translateCity(city),
          citySuffix(city)
        ),
      desktop: (city) =>
        locale.section5.eventTypeDescription.event.desktop(
          translateCity(city),
          citySuffix(city)
        ),
    },
  
    [SEO_SERVICE_BAR_BAT]: {
      mobile: (city) =>
        locale.section5.eventTypeDescription.bar_bat.mobile(
          translateCity(city),
          citySuffix(city)
        ),
      desktop: (city) =>
        locale.section5.eventTypeDescription.bar_bat.desktop(
          translateCity(city),
          citySuffix(city)
        ),
    },
  
    [SEO_SERVICE_HENNA]: {
      mobile: (city) =>
        locale.section5.eventTypeDescription.henna.mobile(
          translateCity(city),
          citySuffix(city)
        ),
      desktop: (city) =>
        locale.section5.eventTypeDescription.henna.desktop(
          translateCity(city),
          citySuffix(city)
        ),
    },
  };

  // ─────────────────────────────────────────────────────────────
  // GALLERY HEADINGS (Section 3)
  // ─────────────────────────────────────────────────────────────
  const GALLERY_HEADINGS_COPY: Record<SeoService, (keyword: string, city: SeoCity) => string[]> = {
    [SEO_SERVICE_WEDDING]: (keyword, city) => locale.section3.galleryHeadings.wedding(keyword, citySuffix(city)),
    [SEO_SERVICE_HENNA]: (keyword, city) => locale.section3.galleryHeadings.henna(keyword, citySuffix(city)),
    [SEO_SERVICE_BAR_BAT]: (keyword, city) => locale.section3.galleryHeadings.bar_bat(keyword, citySuffix(city)),
    [SEO_SERVICE_EVENT]: (keyword, city) => locale.section3.galleryHeadings.event(keyword, citySuffix(city)),
  };

  // ─────────────────────────────────────────────────────────────
  // Section 4 SEO copy (intros + events + services + contact)
  // ─────────────────────────────────────────────────────────────
  type Section4SeoTemplate = (keyword: string, cityBet: string) => string;

  const SECTION4_EVENTS_INTRO_COPY: Record<SeoService, Section4SeoTemplate> = {
    [SEO_SERVICE_WEDDING]: (keyword, cityBet) =>
      isGenericDjKeyword(keyword) ? locale.section4.eventsIntro.default : locale.section4.eventsIntro.wedding(keyword, cityBet),

    [SEO_SERVICE_HENNA]: (keyword, cityBet) =>
      isGenericDjKeyword(keyword) ? locale.section4.eventsIntro.default : locale.section4.eventsIntro.henna(keyword, cityBet),

    [SEO_SERVICE_BAR_BAT]: (keyword, cityBet) =>
      isGenericDjKeyword(keyword) ? locale.section4.eventsIntro.default : locale.section4.eventsIntro.bar_bat(keyword, cityBet),

    [SEO_SERVICE_EVENT]: (keyword, cityBet) =>
      isGenericDjKeyword(keyword) ? locale.section4.eventsIntro.default : locale.section4.eventsIntro.event(keyword, cityBet),
  };

  const SECTION4_SERVICES_INTRO_COPY: Record<SeoService, Section4SeoTemplate> = {
    [SEO_SERVICE_WEDDING]: locale.section4.servicesIntro.wedding,
    [SEO_SERVICE_HENNA]: locale.section4.servicesIntro.henna,
    [SEO_SERVICE_BAR_BAT]: locale.section4.servicesIntro.bar_bat,
    [SEO_SERVICE_EVENT]: locale.section4.servicesIntro.event,
  };

  const SECTION4_CONTACT_COPY: SectionFourSEO['contact'] = locale.section4.contactSeo;

  const SECTION4_EVENTS_TEXTS_TEMPLATE = (cityBet: string): SectionFourSEO['events'] => ({
    [EVENT_GROUP.weddings]: locale.section4.eventsTexts.wedding(cityBet),
    [EVENT_GROUP.hennas]: locale.section4.eventsTexts.henna(cityBet),
    [EVENT_GROUP.barBat]: locale.section4.eventsTexts.bar_bat(cityBet),
    [EVENT_GROUP.events]: locale.section4.eventsTexts.event(cityBet),
  });

  const SECTION4_SERVICES_TEXTS_TEMPLATE: SectionFourSEO['services'] = {
    sound: locale.section4.services.sound.description,
    light: locale.section4.services.light.description,
    attractions: locale.section4.services.attractions.description,
    recording: locale.section4.services.recording.description,
  };

  function buildSection4SeoCopy(
    service: SeoService,
    keyword: string,
    city: SeoCity
  ): SectionFourSEO {
  
    const cityBet = citySuffix(city); // ⭐️ במקום withBet
  
    const eventsIntro = SECTION4_EVENTS_INTRO_COPY[service](
      keyword,
      cityBet
    );
  
    const servicesIntro = SECTION4_SERVICES_INTRO_COPY[service](
      keyword,
      cityBet
    );
  
    return {
      intros: { events: eventsIntro, services: servicesIntro },
      contact: SECTION4_CONTACT_COPY,
      events: SECTION4_EVENTS_TEXTS_TEMPLATE(cityBet),
      services: SECTION4_SERVICES_TEXTS_TEMPLATE,
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Section 4 group titles
  // ─────────────────────────────────────────────────────────────
  const SECTION4_GROUP_TITLES_COPY: Record<
  SeoService,
  (keyword: string, city: SeoCity) => {
    contact: string;
    events: string;
    services: string;
    links: string;
  }
> = {
  [SEO_SERVICE_WEDDING]: (keyword, city) =>
    locale.section4.groupTitlesSeo.wedding(
      keyword,
      cityBetForLocale(city),
      BRAND_TITLE_HE
    ),

  [SEO_SERVICE_HENNA]: (keyword, city) =>
    locale.section4.groupTitlesSeo.henna(
      keyword,
      cityBetForLocale(city),
      BRAND_TITLE_HE
    ),

  [SEO_SERVICE_BAR_BAT]: (keyword, city) =>
    locale.section4.groupTitlesSeo.bar_bat(
      keyword,
      cityBetForLocale(city),
      BRAND_TITLE_HE
    ),

  [SEO_SERVICE_EVENT]: (keyword, city) =>
    locale.section4.groupTitlesSeo.event(
      translateKeywordForLocale(keyword),
      cityBetForLocale(city),
      BRAND_TITLE_HE
    )
};

  const SECTION5_HEADINGS_COPY: Record<SeoService, (keyword: string, city: SeoCity, hasCity: boolean) => string[]> = {
    [SEO_SERVICE_WEDDING]: (keyword, city) => locale.section5.headingsSeo.wedding(keyword, citySuffix(city)),
    [SEO_SERVICE_HENNA]: (keyword, city) => locale.section5.headingsSeo.henna(keyword, citySuffix(city)),
    [SEO_SERVICE_BAR_BAT]: (keyword, city) => locale.section5.headingsSeo.bar_bat(keyword, citySuffix(city)),
    [SEO_SERVICE_EVENT]: (keyword, city, hasCity) => locale.section5.headingsSeo.event(keyword, citySuffix(city), hasCity),
  };

  // ─────────────────────────────────────────────────────────────
  // EXTRA COPY — factory helpers (Section 1/2/4/6 + hubs + home)
  // ─────────────────────────────────────────────────────────────
  const SECTION1_TARGET_DESCRIPTION_LINES_BASE = locale.section1.targetPageLines.base;

  const SECTION1_TARGET_DESCRIPTION_LINES_BY_SERVICE: Record<SeoService, readonly string[]> = {
    [SEO_SERVICE_WEDDING]: locale.section1.targetPageLines.wedding,
    [SEO_SERVICE_HENNA]: locale.section1.targetPageLines.henna,
    [SEO_SERVICE_BAR_BAT]: locale.section1.targetPageLines.bar_bat,
    [SEO_SERVICE_EVENT]: locale.section1.targetPageLines.event,
  };

  const buildSection1TargetSignatureLine = (keyword: string, cityOrLabel: string) => {
    const suffix = makeCitySuffix(locale, cityOrLabel);
    const normalizedKeyword = String(keyword).replace(/דיג׳יי/g, 'די ג׳יי');
    return `${BRAND_TITLE_SHORT} - ${normalizedKeyword}${suffix}`.trim();
  };
  function buildSection1TargetDescriptionLines(service: SeoService, keyword: string, cityOrLabel: SeoCity | string): string[] {
    const base = SECTION1_TARGET_DESCRIPTION_LINES_BY_SERVICE[service] ?? SECTION1_TARGET_DESCRIPTION_LINES_BASE;
    const signatureKeyword = secondaryKeywordForService(service) || keyword;
    return [...base, buildSection1TargetSignatureLine(signatureKeyword, String(cityOrLabel))];
  }

  const SECTION2_TARGET_ADDITIONAL_BY_SERVICE: Record<SeoService, string> = {
    [SEO_SERVICE_WEDDING]: locale.section2.targetAdditionalByService.wedding,
    [SEO_SERVICE_HENNA]: locale.section2.targetAdditionalByService.henna,
    [SEO_SERVICE_BAR_BAT]: locale.section2.targetAdditionalByService.bar_bat,
    [SEO_SERVICE_EVENT]: locale.section2.targetAdditionalByService.event,
  };

  const buildSection2TargetDescription = (baseDescription: string, djKeyword: string, cityOrLabel: string) =>
    locale.section2.targetDescription(baseDescription, djKeyword, makeCitySuffix(locale, cityOrLabel));

  const SECTION2_TARGET_ADDITIONAL_SUFFIX_BY_SERVICE: Record<SeoService, string> = {
    [SEO_SERVICE_WEDDING]: locale.section2.targetAdditionalSuffixByService.wedding,
    [SEO_SERVICE_HENNA]: locale.section2.targetAdditionalSuffixByService.henna,
    [SEO_SERVICE_BAR_BAT]: locale.section2.targetAdditionalSuffixByService.bar_bat,
    [SEO_SERVICE_EVENT]: locale.section2.targetAdditionalSuffixByService.event,
  };

  const buildSection2TargetAdditionalText = (service: SeoService, baseAdditional: string, cityOrLabel: SeoCity | string) => {
    const citySuffix = makeCitySuffix(locale, String(cityOrLabel));

    const base = SECTION2_TARGET_ADDITIONAL_BY_SERVICE[service] ?? baseAdditional;
    const suffix = SECTION2_TARGET_ADDITIONAL_SUFFIX_BY_SERVICE[service] ?? locale.section2.fallbackSuffix;

    // locale function expects "cityBet" param — we now pass suffix like "בX" / " in X"
    return locale.section2.targetAdditionalText(base, citySuffix, suffix);
  };


  function buildSection4BlockCopy(keyword: string, cityName: string) {
    const hasCity = cityName.trim().length > 0;

    if (hasCity) {
      return locale.section4.blockCopy.withCity(keyword, cityName, locale.brand.firstName);
    }

    return locale.section4.blockCopy.withoutCity(keyword, locale.words.events.generic.one, locale.brand.firstName);
  }

  const buildSection6DefaultTitle = (_keyword: string, _cityOrLabel: SeoCity | string) =>
    locale.section6.defaults.title(locale.brand.title);

  const buildSection6DefaultSubtitle = (keyword: string, cityOrLabel: SeoCity | string) =>
    locale.section6.defaults.subtitle(
      locale.brand.short,
      keyword,
      cityBetForLocale(cityOrLabel)
    );
  const buildCityHubSection1DescriptionLines = (keyword: string, city: SeoCity) =>
    locale.section1.hub.city.descriptionLines(keyword, makeCitySuffix(locale, city), locale.brand.title);

  const buildCityHubSection1ExtraHeadings = (keyword: string, city: SeoCity) =>
    locale.section1.hub.city.extraHeadings(keyword, makeCitySuffix(locale, city));

  const buildCityHubSection3ExtraHeadings = (city: SeoCity) => {
    const suffix = makeCitySuffix(locale, city);
    // Keep your original Hebrew copy; if you want EN here too, we can add it later.
    return [`רגעים מהרחבה${suffix}`];
  };
  const buildRegionHubSection1DescriptionLines = (keyword: string, regionLabel: string) =>
    locale.section1.hub.region.descriptionLines(keyword, makeCitySuffix(locale, regionLabel), locale.brand.title);

  const buildRegionHubSection1ExtraHeadings = (keyword: string, regionLabel: string) =>
    locale.section1.hub.region.extraHeadings(keyword, makeCitySuffix(locale, regionLabel));

  const HOME_SECTION1_DESCRIPTION_LINES = locale.home.sections.section1Lines(
    WORDS.events.generic.one,
    WORDS.mc,
    locale.brand.title
  );

  const HOME_SECTION2_DESCRIPTION_SUFFIX = locale.home.sections.section2DescriptionSuffix(WORDS.events.generic.one);
  const HOME_SECTION2_ADDITIONAL_SUFFIX = locale.home.sections.section2AdditionalSuffix;

  const HOME_SECTION5_SUBTITLE_DESKTOP = locale.home.sections.section5SubtitleDesktop(
    WORDS.events.wedding.one,
    WORDS.events.henna.one,
    WORDS.events.bar_bat.one,
    WORDS.events.generic.one
  );

  const HOME_SECTION5_SUBTITLE_MOBILE = locale.home.sections.section5SubtitleMobile(
    WORDS.events.wedding.many,
    WORDS.events.henna.many,
    WORDS.events.bar_bat.one,
    WORDS.events.generic.many,
    WORDS.events.generic.one
  );

  const HOME_SECTION6_TITLE = locale.home.sections.section6Title(locale.brand.title);
  const HOME_SECTION6_SUBTITLE = locale.home.sections.section6Subtitle(WORDS.events.generic.one);

  const MENU_SECTIONS = locale.menu.sections as readonly {
    id: string;
    label: string;
  }[];

  const ALBUM_LABELS: Record<AlbumName, string> = {
    חתונות: WORDS.events.wedding.many,
    חינות: WORDS.events.henna.many,
    'בר/בת מצוות': WORDS.events.bar_bat.many,
    פורום: WORDS.albums.forum,
    כתבות: WORDS.albums.articles,
    המלצות: WORDS.albums.recommendations,
    ברים: WORDS.albums.bars,
    פלאיירים: WORDS.albums.flyers,
  };

  const FAQ_RAW: any = locale.seo?.faq ?? {};

  // Map: SeoService -> canonical FAQ key in locale packs (en keys)
  const FAQ_KEY_BY_SERVICE: Record<SeoService, string> = {
    [SEO_SERVICE_WEDDING]: 'wedding',
    [SEO_SERVICE_HENNA]: 'henna',
    [SEO_SERVICE_BAR_BAT]: 'bar_bat',
    [SEO_SERVICE_EVENT]: 'event',
  } as const;
  
  function getFaqArrayForService(service: SeoService): any[] {
    // 1) Try exact key (in case locale uses Hebrew keys identical to SeoService)
    const direct = FAQ_RAW?.[service];
    if (Array.isArray(direct) && direct.length) return direct;
  
    // 2) Try canonical EN key (wedding/henna/bar_bat/event)
    const k = FAQ_KEY_BY_SERVICE[service];
    const byCanonical = FAQ_RAW?.[k];
    if (Array.isArray(byCanonical) && byCanonical.length) return byCanonical;
  
    // 3) Try common Hebrew keys (if locale uses Hebrew keys but service is different)
    //    (these are safe fallbacks and won't break if not present)
    const altHebKeys =
      service === SEO_SERVICE_WEDDING
        ? ['חתונה']
        : service === SEO_SERVICE_HENNA
          ? ['חינה']
          : service === SEO_SERVICE_BAR_BAT
            ? ['בר-מצווה', 'בר/בת מצווה', 'בר בת מצווה']
            : ['אירוע'];
  
    for (const hk of altHebKeys) {
      const arr = FAQ_RAW?.[hk];
      if (Array.isArray(arr) && arr.length) return arr;
    }
  
    return [];
  }

  function getVenueFaqEventCopy(service: SeoService) {
    const lang = locale.meta.lang;

    if (lang === 'he') {
      if (service === SEO_SERVICE_WEDDING) return { eventLabel: 'חתונה' };
      if (service === SEO_SERVICE_HENNA) return { eventLabel: 'חינה' };
      if (service === SEO_SERVICE_BAR_BAT) return { eventLabel: 'בר/בת מצווה' };
      return { eventLabel: 'אירוע' };
    }

    if (lang === 'fr') {
      if (service === SEO_SERVICE_WEDDING) return { eventLabel: 'un mariage' };
      if (service === SEO_SERVICE_HENNA) return { eventLabel: 'une henné' };
      if (service === SEO_SERVICE_BAR_BAT) return { eventLabel: 'une bar/bat mitzvah' };
      return { eventLabel: 'un événement' };
    }

    if (lang === 'ru') {
      if (service === SEO_SERVICE_WEDDING) return { eventLabel: 'свадьбы' };
      if (service === SEO_SERVICE_HENNA) return { eventLabel: 'хины' };
      if (service === SEO_SERVICE_BAR_BAT) return { eventLabel: 'бар/бат-мицвы' };
      return { eventLabel: 'события' };
    }

    if (service === SEO_SERVICE_WEDDING) return { eventLabel: 'wedding', article: 'a' };
    if (service === SEO_SERVICE_HENNA) return { eventLabel: 'henna', article: 'a' };
    if (service === SEO_SERVICE_BAR_BAT) return { eventLabel: 'bar/bat mitzvah', article: 'a' };
    return { eventLabel: 'event', article: 'an' };
  }

  function buildVenueListFaq(service: SeoService, city: SeoCity | '') {
    if (!city) return undefined;

    const venues = getCityVenues(city);
    if (!venues.length) return undefined;

    const cityLabel = translateCity(city);
    const venuesText = venues.join(', ');
    const eventCopy = getVenueFaqEventCopy(service);

    if (locale.meta.lang === 'he') {
      return {
        q: `באילו אולמות אפשר לקיים ${eventCopy.eventLabel} בעיר ${cityLabel}?`,
        a: `בעיר ${cityLabel} יש כמה אולמות ומתחמי אירועים שכדאי לבדוק לפני שסוגרים ${eventCopy.eventLabel}: ${venuesText}. חשוב לוודא מול כל אולם זמינות, התאמה לכמות האורחים, חניה, גודל רחבת הריקודים, אקוסטיקה ומגבלות סאונד.`,
      };
    }

    if (locale.meta.lang === 'fr') {
      return {
        q: `Quelles salles vérifier pour ${eventCopy.eventLabel} à ${cityLabel} ?`,
        a: `À ${cityLabel}, les salles et lieux à vérifier pour ${eventCopy.eventLabel} incluent : ${venuesText}. Avant de réserver, vérifiez la disponibilité, la capacité, le parking, la taille de la piste, l’acoustique et les limites de son avec chaque lieu.`,
      };
    }

    if (locale.meta.lang === 'ru') {
      return {
        q: `Какие площадки стоит проверить для ${eventCopy.eventLabel} в ${cityLabel}?`,
        a: `В ${cityLabel} среди площадок, которые стоит проверить для ${eventCopy.eventLabel}: ${venuesText}. Перед бронированием важно уточнить доступность даты, вместимость, парковку, размер танцпола, акустику и ограничения по звуку в каждом месте.`,
      };
    }

    return {
      q: `Which venues are worth checking for ${eventCopy.article} ${eventCopy.eventLabel} in ${cityLabel}?`,
      a: `In ${cityLabel}, venues worth checking for ${eventCopy.article} ${eventCopy.eventLabel} include: ${venuesText}. Before booking, verify availability, guest capacity, parking, dance-floor size, acoustics and sound limits with each venue.`,
    };
  }
  
  function buildFaq(service: SeoService, city: SeoCity | '') {
    const raw =
      getFaqArrayForService(service) ||
      FAQ_RAW?.homeEvent || // ⭐ fallback לדף הבית אם אין התאמה
      [];
  
    const cityLabel = city ? translateLocationLabel(city) : '';
    const cityText = cityLabel ? makeCitySuffix(locale, cityLabel) : '';
    const citySentenceText = cityLabel ? makeSentenceCityPrefix(locale, cityLabel) : '';
    const citySentencePattern = citySentenceText
      ? new RegExp(
          `^(${citySentenceText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+)(\\p{Lu})`,
          'u'
        )
      : null;
  
    const cleanFaqText = (value: unknown) => {
      const text = String(value ?? '')
        .replace(/^\s*\{city\}/g, citySentenceText)
        .replace(/\{city\}/g, cityText)
        .replace(/\s+([?!.,:;])/g, '$1')
        .replace(/\s+/g, ' ')
        .trim();

      return citySentencePattern
        ? text.replace(
            citySentencePattern,
            (_match, prefix: string, firstLetter: string) =>
              `${prefix}${firstLetter.toLocaleLowerCase(locale.meta.langFull || locale.meta.lang)}`
          )
        : text;
    };

    const faqs = (Array.isArray(raw) ? raw : []).map((item: any) => ({
      q: cleanFaqText(item.q),
      a: cleanFaqText(item.a),
    }));

    const venueListFaq = buildVenueListFaq(service, city);
    return venueListFaq ? [...faqs, venueListFaq] : faqs;
  }

  function translateRegion(region: Region | string): string {
    const r = String(region ?? '').trim();
    if (!r) return '';
    // he -> returns same, en -> map
    return (locale.geo?.regions?.[r as Region] as string) || r;
  }
  
  function translateCity(city: SeoCity | string): string {
    const c = String(city ?? '').trim();
    if (!c) return '';
    // he -> returns same, en -> transliteration/common spelling
    return (locale.geo?.cities?.[c as SeoCity] as string) || c;
  }

  const venuesByCity =
    (locale.geo?.venuesByCity ?? heLocale.geo?.venuesByCity ?? {}) as Record<string, readonly string[]>;

  function getCityVenues(city: SeoCity | string): string[] {
    const c = String(city ?? '').trim();
    if (!c) return [];
    return [...(venuesByCity[c] ?? [])];
  }
  
  /**
   * Used for UI labels (title/description/link labels) when we have a "locationLabel"
   * that can be either a Region or a City (but slug itself remains Hebrew).
   */
  function translateLocationLabel(label: string): string {
    const raw = String(label ?? '').trim();
    if (!raw) return '';
    if (isRegionName(raw)) return translateRegion(raw as Region);
    return translateCity(raw as SeoCity);
  }

  const SECTION6_REVIEWS_COUNT = (count: number) =>
    locale.section6.reviewsCount(count);

  const PLANNING_ARTICLES = locale.planning.articles;
  const PLANNING_SECTIONS = locale.planning.sections;
  const PLANNING_READ_MORE_LABEL = locale.planning.readMoreLabel;
  const PLANNING_PREVIOUS_LABEL = locale.planning.previousLabel;
  const PLANNING_NEXT_LABEL = locale.planning.nextLabel;
  const PLANNING_BACK_HOME_LABEL = locale.planning.backHomeLabel;
  const PLANNING_ARTICLE_EYEBROW = (service: string) =>
    locale.planning.articleEyebrow(service);
  const PLANNING_CONTACT_TITLE = locale.planning.contactTitle;
  const PLANNING_CONTACT_BUTTON_LABEL = locale.planning.contactButtonLabel;
  const PLANNING_WHATSAPP_LABEL = locale.planning.whatsappLabel;
  const PLANNING_PHONE_LABEL = locale.planning.phoneLabel;
  const PLANNING_PARENT_LINK_LABEL = (serviceLabel: string) =>
    locale.planning.parentLinkLabel(serviceLabel);
  const PLANNING_PARENT_CONTEXT = (articleTitle: string, serviceLabel: string) =>
    locale.planning.parentContext(articleTitle, serviceLabel);
  const PLANNING_WHATSAPP_TEXT = (articleTitle: string) =>
    locale.planning.whatsappText(articleTitle);
  const PLANNING_TRUST_TITLE = locale.planning.trustTitle;
  const PLANNING_TRUST_PARAGRAPHS = (
    articleTitle: string,
    serviceLabel: string,
    brandShort: string
  ) => locale.planning.trustParagraphs(articleTitle, serviceLabel, brandShort);
  const PLANNING_WORK_TITLE = locale.planning.workTitle;
  const PLANNING_WORK_BULLETS = locale.planning.workBullets;
  const PLANNING_CAROUSEL_LABEL = (title: string) =>
    locale.planning.carouselLabel(title);
  const PLANNING_GUIDE_TITLE = locale.planning.guideTitle;
  const PLANNING_GUIDE_SUBTITLE = locale.planning.guideSubtitle;
  const PLANNING_RELATED_TITLE = locale.planning.relatedTitle;
  const PLANNING_FAQ_TITLE = locale.planning.faqTitle;
  const PLANNING_AUTHOR_TITLE = locale.planning.authorTitle;
  const PLANNING_AUTHOR_TEXT = locale.planning.authorText;
  const PLANNING_CITY_VENUE_GUIDE = locale.planning.cityVenueGuide;

    // ─────────────────────────────────────────────────────────────
  // Return SSOT object
  // ─────────────────────────────────────────────────────────────
  return {
    // Brand/Words
    WORDS,
    FIRST_NAME,
    BRAND_TITLE_SHORT,
    BRAND_TITLE_HE,

    // Business
    BUSINESS,

    // HTML/Home/Footer
    HTML_LANG,
    HTML_LANG_FULL,
    HOME_TITLE,
    HOME_DESC,
    HOME_HERO_TITLE,
    FOOTER_TEXT,

    // Section2
    SECTION_TWO_TITLE,
    SECTION_TWO_DESCRIPTION,
    SECTION_TWO_ADDITIONAL_TEXT,
    SECTION_TWO_IMG_ALT,
    SECTION_TWO_HORIZONTAL_IMG_ALT,

    // Section3
    SECTION_THREE_TITLE,
    SECTION_THREE_FALLBACK_TEXT,
    SECTION_THREE_ALBUMS_ERROR,
    SECTION_THREE_FILES_ERROR,
    SECTION_THREE_LOADING_TEXT,
    SECTION_THREE_VIDEO_LABEL,
    SECTION_THREE_PHOTOS_LABEL,
    SECTION_THREE_NEXT_ITEM_LABEL,
    SECTION_THREE_PREV_ITEM_LABEL,
    SECTION_THREE_CAROUSEL_LABEL,
    SECTION_THREE_CURRENT_ITEM_LABEL,
    albumDescriptions,
    albumsText,

    // Section4
    SECTION_FOUR_TITLE,
    SECTION_FOUR_DESCRIPTION,
    SECTION_FOUR_CONTACT_GROUP_TITLE,
    SECTION_FOUR_EVENTS_GROUP_TITLE,
    SECTION_FOUR_SERVICES_GROUP_TITLE,
    SECTION_FOUR_CONTACT_BUTTONS,
    SECTION_FOUR_EVENT_BUTTONS,
    SECTION_FOUR_SERVICE_BUTTONS,

    // Section5
    SECTION_FIVE_TITLE,
    SECTION_FIVE_LINKS_HEADING,

    // Meta/SEO helpers
    makeMetaDescription,
    BRAND_PREFIX_RE,
    BRAND_SUFFIX_RE,
    SEO_KEYWORD_VARIANTS,
    preferredKeyword,
    makeTitle,
    preferredKeywordForService,
    secondaryKeywordForService,
    buildWhatsAppWithText,

    // Factories / SEO copy maps
    SECTION_THREE_DEFAULT_ALBUM_BY_SERVICE,
    EVENT_TYPE_DESCRIPTION_COPY,
    GALLERY_HEADINGS_COPY,
    buildSection4SeoCopy,
    SECTION4_GROUP_TITLES_COPY,
    SECTION5_HEADINGS_COPY,

    // Extra copy builders
    SECTION1_TARGET_DESCRIPTION_LINES_BASE,
    SECTION1_TARGET_DESCRIPTION_LINES_BY_SERVICE,
    buildSection1TargetSignatureLine,
    buildSection1TargetDescriptionLines,
    SECTION2_TARGET_ADDITIONAL_BY_SERVICE,
    buildSection2TargetDescription,
    SECTION2_TARGET_ADDITIONAL_SUFFIX_BY_SERVICE,
    buildSection2TargetAdditionalText,
    buildSection4BlockCopy,
    buildSection6DefaultTitle,
    buildSection6DefaultSubtitle,
    buildCityHubSection1DescriptionLines,
    buildCityHubSection1ExtraHeadings,
    buildCityHubSection3ExtraHeadings,
    buildRegionHubSection1DescriptionLines,
    buildRegionHubSection1ExtraHeadings,

    // Home computed
    HOME_SECTION1_DESCRIPTION_LINES,
    HOME_SECTION2_DESCRIPTION_SUFFIX,
    HOME_SECTION2_ADDITIONAL_SUFFIX,
    HOME_SECTION5_SUBTITLE_DESKTOP,
    HOME_SECTION5_SUBTITLE_MOBILE,
    HOME_SECTION6_TITLE,
    HOME_SECTION6_SUBTITLE,

    MENU_SECTIONS,
    ALBUM_LABELS,
    FAQ_BY_SERVICE: FAQ_RAW,
    buildFaq,
    translateCity,
    translateRegion,
    translateLocationLabel,
    venuesByCity,
    getCityVenues,
    footer: {
      recommendedPages: locale.footer.recommendedPages,
      addressLabel: locale.footer.addressLabel,
      addresses: locale.footer.addresses,
      accessibilityStatement: locale.footer.accessibilityStatement,
      copyright: locale.footer.copyright,
    },
    SECTION6_REVIEWS_COUNT,
    PLANNING_ARTICLES,
    PLANNING_SECTIONS,
    PLANNING_READ_MORE_LABEL,
    PLANNING_PREVIOUS_LABEL,
    PLANNING_NEXT_LABEL,
    PLANNING_BACK_HOME_LABEL,
    PLANNING_ARTICLE_EYEBROW,
    PLANNING_CONTACT_TITLE,
    PLANNING_CONTACT_BUTTON_LABEL,
    PLANNING_WHATSAPP_LABEL,
    PLANNING_PHONE_LABEL,
    PLANNING_PARENT_LINK_LABEL,
    PLANNING_PARENT_CONTEXT,
    PLANNING_WHATSAPP_TEXT,
    PLANNING_TRUST_TITLE,
    PLANNING_TRUST_PARAGRAPHS,
    PLANNING_WORK_TITLE,
    PLANNING_WORK_BULLETS,
    PLANNING_CAROUSEL_LABEL,
    PLANNING_GUIDE_TITLE,
    PLANNING_GUIDE_SUBTITLE,
    PLANNING_RELATED_TITLE,
    PLANNING_FAQ_TITLE,
    PLANNING_AUTHOR_TITLE,
    PLANNING_AUTHOR_TEXT,
    PLANNING_CITY_VENUE_GUIDE,
  } as const;
}

// Optional convenience selector (dynamic)
export async function createI18nAsync(lang: AppLang): Promise<I18nConstants> {
  switch (lang) {
    case 'he': {
      const { he } = await import('@/i18n/locales/he');
      return createI18nConstants(he);
    }
    case 'en': {
      const { en } = await import('@/i18n/locales/en');
      return createI18nConstants(en);
    }
    case 'ru': {
      const { ru } = await import('@/i18n/locales/ru');
      return createI18nConstants(ru as any);
    }
    case 'fr': {
      const { fr } = await import('@/i18n/locales/fr');
      return createI18nConstants(fr as any);
    }
    default:
      const { he } = await import('@/i18n/locales/he');
      return createI18nConstants(he);
  }
}
